import React, { useEffect, useState } from "react";
import {
  TelephoneFill,
  ChevronRight,
  BagFill,
  ChatDots,
  Calendar,
  X,
  Check2,
  Person,
  Telephone,
} from "react-bootstrap-icons";

import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import {
  getStoreCustomerPendingOrder,
  updateOrderPayment,
} from "../../services/storeOwnerOrderServices";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { notify } from "../../components/Common/Alerts";
import { formatCurrencyINR } from "../../utils/function";
import { useConfig } from "../../components/Context/ConfigContext";
import { useSelector } from "react-redux";

const OrderSkeleton = () => (
  <div className="bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-[2rem] shadow-sm p-4 animate-pulse transition-colors duration-300">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-gray-300 dark:bg-slate-700 rounded-2xl"></div>

        <div className="flex flex-col gap-1">
          <div className="w-24 h-4 bg-gray-300 dark:bg-slate-700 rounded"></div>
          <div className="w-32 h-3 bg-gray-200 dark:bg-slate-800 rounded"></div>
        </div>
      </div>

      <div className="text-right flex flex-col gap-1 items-end">
        <div className="w-16 h-3 bg-gray-200 dark:bg-slate-800 rounded"></div>
        <div className="w-20 h-5 bg-gray-300 dark:bg-slate-700 rounded"></div>
      </div>
    </div>

    <div className="flex items-center justify-between gap-3 mt-2">
      <div className="flex gap-2">
        <div className="w-10 h-10 bg-gray-200 dark:bg-slate-800 rounded-xl"></div>
        <div className="w-10 h-10 bg-gray-200 dark:bg-slate-800 rounded-xl"></div>
      </div>
      <div className="flex-1 h-10 bg-gray-300 dark:bg-slate-700 rounded-xl"></div>
    </div>
  </div>
);

const PendingOrdersList = () => {
  const location = useLocation();
  const customer = location.state?.item;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { configData } = useConfig();
  const { uuid } = useParams();
  let auth = useSelector((x) => x.auth.value);

  const { pendingOrderList, setPendingOrderList } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [formdata, setFormdata] = useState({
    order_uuid: "",
    amount: "",
    payment_method: "",
  });
  const [errors, setErrors] = useState({
    amount: "",
    payment_method: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentData, setPaymentData] = useState("");
  const [paymentmethod, setPaymentMethod] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const handlePaymentOpenModel = (e, item) => {
    setIsPaymentModalOpen(true);
    setPaymentData(item);
  };

  const validateField = (name, value) => {
    switch (name) {
      case "amount":
        if (!value) return t("amountIsRequired");
        return "";
      case "payment_method":
        if (!value) return t("paymentMethodIsRequired");
        return "";

      default:
        return "";
    }
  };

  const filteredOrders = pendingOrderList?.pendingOrders?.filter((order) =>
    order.order_number.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    if (customer) {
      setLoading(true);
      const data = { customer_id: customer.id, store_id: auth.active_store_id };

      getStoreCustomerPendingOrder(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);
          setPendingOrderList(decrypted.data);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [customer, auth.active_store_id]);

  let walletBalance = Number(pendingOrderList?.total_balance || 0);
  let totalAmount = Number(paymentData?.total_amount || 0);
  const isWalletSelected = Number(paymentmethod) === 3;
  let isInsufficientBalance = isWalletSelected && walletBalance < totalAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {
      payment_method: validateField("payment_method", paymentmethod),
    };

    if (!isWalletSelected) {
      newErrors.amount = validateField("amount", formdata.amount);
    }

    Utils.focusError();
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((err) => err);

    if (hasErrors) return;

    setIsSubmitting(true);

    let finalAmount;

    if (isWalletSelected) {
      finalAmount = walletBalance >= totalAmount ? totalAmount : walletBalance;
    } else {
      finalAmount = formdata.amount;
    }

    const payload = {
      order_uuid: paymentData?.uuid,
      amount: finalAmount,
      payment_method: paymentmethod,
      store_id: auth.active_store_id,
    };

    try {
      const response = await updateOrderPayment(payload);
      const decrypted = decryptData(response.data.data);

      notify.success(decrypted.message);
      setFormdata({
        order_uuid: "",
        amount: "",
        payment_method: "",
      });
      setErrors({
        amount: "",
        payment_method: "",
      });
      setIsPaymentModalOpen(false);
      navigate(`/pending-order/${uuid}`);
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const callCustomer = (mobile) => {
    if (!mobile) return;
    const phone = String(mobile).replace(/\s+/g, "");
    window.location.href = `tel:${phone}`;
  };

  return (
    <div className="min-h-screen bg-orange-50/50 dark:bg-slate-950 p-4 pb-24 flex flex-col transition-colors duration-300">
      <div className="mb-6 flex flex-col gap-3">
        {/* Bottom row: Back button + Title + Orders count */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard")}
            className="hidden md:flex p-1 rounded hover:bg-orange-100 dark:hover:bg-slate-800 text-orange-600 dark:text-orange-500 hover:text-orange-800 transition"
            aria-label="Go back"
          >
            <ChevronLeft size={20} />
          </button>

          <h2 className="hidden md:flex flex-1 text-xl font-black text-zinc-900 dark:text-white flex items-center gap-2 mx-4 whitespace-nowrap">
            <div className="w-2 h-6 bg-orange-500 rounded-full"></div>
            {pendingOrderList?.customer?.name}'{t("SOrders")}
          </h2>

          <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-[10px] font-bold px-3 py-1 rounded-full uppercase whitespace-nowrap">
            {filteredOrders?.length || 0} {t("orders")}
          </span>
        </div>

        <input
          type="text"
          placeholder={t("searchOrders")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full border border-orange-400 dark:border-slate-700 bg-white dark:bg-slate-900 text-zinc-900 dark:text-slate-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      {/* Orders List */}
      <div className="flex-1 overflow-y-auto space-y-4">
        {loading
          ? Array.from({ length: 3 }).map((_, index) => (
              <OrderSkeleton key={index} />
            ))
          : filteredOrders?.map((order, index) => {
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-slate-900 border border-orange-100 dark:border-slate-800 rounded-[2rem] shadow-sm overflow-hidden cursor-pointer hover:border-orange-400 dark:hover:border-orange-500 hover:shadow-md transition-all duration-200 p-4"
                  onClick={() =>
                    navigate(`/all-store-owner-order-details/${order.uuid}`)
                  }
                  // onTouchEnd={(e) => e.currentTarget.click()}
                >
                  {/* Top Section */}
                  <div className="p-4 flex items-center justify-between border-b border-orange-50 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center text-white font-bold text-sm shadow-md">
                        #{order.order_number.slice(-4)}
                      </div>
                      <div>
                        <h3 className="font-bold text-zinc-900 dark:text-slate-100 leading-tight">
                          #{order.order_number}
                        </h3>

                        <p className="text-[11px] text-zinc-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                          <Calendar size={12} />
                          {order.created_at}
                        </p>

                        <p className="text-[11px] text-zinc-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                          <Person size={12} />
                          {order.customer_name}
                        </p>

                        <p className="text-[11px] text-zinc-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                          <Telephone size={12} />
                          {order.customer_mobile}
                        </p>
                      </div>
                    </div>

                    {/* Right */}
                    <div className="text-right">
                      <span className="text-[10px] font-bold text-orange-500 uppercase bg-orange-50 dark:bg-orange-950 px-2 py-0.5 rounded">
                        {order.status_label}
                      </span>

                      <p className="text-lg font-black text-zinc-900 dark:text-white mt-1">
                        {formatCurrencyINR(order.pending_amount)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-zinc-50/50 dark:bg-slate-800/50 px-4 py-3 flex items-center justify-between gap-3">
                    <div className="flex gap-2">
                      <a
                        href={`tel:+91${String(pendingOrderList?.customer?.mobile || "").replace(/\D/g, "")}`}
                        onClick={(e) => e.stopPropagation()}
                        className="w-10 h-10 bg-white dark:bg-slate-800 border border-zinc-200 dark:border-slate-700 rounded-xl flex items-center justify-center text-black-500 active:bg-black-500 active:text-white transition-all shadow-sm"
                      >
                        <TelephoneFill size={18} />
                      </a>
                      <a
                        href={`https://wa.me/91${pendingOrderList?.customer?.mobile}`}
                        target="_blank"
                        rel="noreferrer"
                        className="w-10 h-10 bg-white dark:bg-slate-800 border border-zinc-200 dark:border-slate-700 rounded-xl flex items-center justify-center text-green-500 active:bg-green-500 active:text-white transition-all shadow-sm"
                        onClick={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                      >
                        <ChatDots size={18} />
                      </a>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePaymentOpenModel(e, order);
                      }}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 rounded-xl text-sm flex items-center justify-center gap-2 shadow-lg shadow-orange-200 dark:shadow-none transition-all active:scale-95"
                    >
                      <BagFill size={14} />
                      {t("updatePayment")}
                      <ChevronRight size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
      </div>

      {/* Payment Modal */}
      {isPaymentModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-md" />
          <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-t-[2.5rem] md:rounded-[2rem] shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300">
            <div className="p-8 border-b dark:border-slate-800 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50">
              <h3 className="text-2xl font-black text-gray-900 dark:text-white">
                {t("updatePayment")}
              </h3>
              <button
                onClick={() => {
                  setIsPaymentModalOpen(false);
                  setErrors({ amount: "", payment_method: "" });
                  setFormdata({
                    order_uuid: "",
                    amount: "",
                    payment_method: "",
                  });
                  setPaymentMethod("");
                }}
                className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full transition-colors text-zinc-500 dark:text-slate-400"
              >
                <X size={28} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div>
                <label className="input-label dark:text-slate-300">
                  {t("paymentMethod")}
                </label>
                <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                  {configData?.payment_method?.slice(1).map((type) => {
                    const isWallet = Number(type.value) === 3;
                    const isZeroBalance =
                      Number(pendingOrderList?.total_balance || 0) === 0;
                    const isDisabled = isWallet && isZeroBalance;

                    return (
                      <button
                        type="button"
                        key={type.value}
                        disabled={isDisabled}
                        onClick={() => {
                          if (!isDisabled) {
                            setPaymentMethod(type.value);
                          }
                        }}
                        className={`flex-1 py-3 px-2 rounded-xl font-bold text-sm transition-all flex flex-col items-center justify-center text-center group
                                 ${
                                   isDisabled
                                     ? "opacity-50 cursor-not-allowed"
                                     : paymentmethod === type.value
                                       ? "bg-white dark:bg-slate-900 text-[#E67E22] shadow-sm ring-1 ring-slate-200 dark:ring-slate-700"
                                       : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                                 }`}
                      >
                        <span className="uppercase tracking-wider text-[11px] opacity-80">
                          {type.label}
                        </span>

                        {isWallet && (
                          <span
                            className={`text-sm mt-0.5 font-black ${
                              paymentmethod === type.value
                                ? "text-slate-900 dark:text-white"
                                : "text-slate-400"
                            }`}
                          >
                            {formatCurrencyINR(pendingOrderList?.total_balance)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {errors.payment_method && (
                  <span className={`text-danger mt-1`}>
                    {errors.payment_method}
                  </span>
                )}
              </div>

              {!isWalletSelected && (
                <div>
                  <label className="input-label dark:text-slate-300">
                    {t("amount")}
                  </label>

                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={formdata.amount}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 4);
                      setFormdata({ ...formdata, amount: value });
                    }}
                    placeholder={t("enterAmount")}
                    className={`input-field dark:bg-slate-800 dark:border-slate-700 dark:text-white ${
                      errors.amount ? "input-error" : ""
                    }`}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key))
                        e.preventDefault();
                    }}
                  />

                  {errors.amount && (
                    <span className="text-danger mt-1">{errors.amount}</span>
                  )}
                </div>
              )}

              {isWalletSelected && (
                <div className="input-field flex justify-between items-center">
                  <span className="text-sm text-slate-400">
                    {walletBalance >= totalAmount
                      ? t("paidFromWallet")
                      : t("remainingAmount")}
                  </span>

                  <span className="font-bold text-[#E67E22]">
                    {formatCurrencyINR(
                      walletBalance >= totalAmount
                        ? totalAmount
                        : totalAmount - walletBalance,
                    )}
                  </span>
                </div>
              )}

              <div className="pt-4 grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsPaymentModalOpen(false);
                    setErrors({ amount: "", payment_method: "" });
                    setFormdata({
                      order_uuid: "",
                      amount: "",
                      payment_method: "",
                    });
                    setPaymentMethod("");
                  }}
                  className="py-4 border-2 border-gray-100 dark:border-slate-800 rounded-2xl font-black text-gray-400 dark:text-slate-500 hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="py-4 bg-orange-600 hover:bg-orange-700 text-white rounded-2xl font-black shadow-lg shadow-orange-100 dark:shadow-none flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    t("saving")
                  ) : (
                    <>
                      <Check2 size={24} /> {t("update")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingOrderList?.pendingOrders?.length === 0 && (
        <div className="text-center text-zinc-500 dark:text-slate-500 mt-10">
          {t("noPendingOrders")}
        </div>
      )}
    </div>
  );
};

export default PendingOrdersList;
